import {
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  IS_BOX_PLOT_CHART,
  IS_MOSAIC_CHART,
  IS_STACKED_BAR_CHART
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { resolveFontFamily } from "constants/style.constants";
import { calculateTextDimensions } from "utils/dom";

import { IChartScreenshotSettings } from "models/chart";

import { calculateBoxPlotBarWidth } from "components/charts/utils/calculateBoxPlotBarWidth";
import { getMosaicLabels } from "components/charts/utils/getMosaicLabels";
import { MOSAIC_GRID_LEFT, MOSAIC_GRID_RIGHT } from "components/charts/utils/mosaicUtils";

const applyMosaicLabels = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  screenshotOptions: any,
  screenshotSettings: IChartScreenshotSettings,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiResponse: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  rotation: number
) => {
  const { series, layout } = apiResponse;
  if (!series?.length) return;

  // Resolve the current visible x-axis range from the zoom state so that label
  // density is recalculated for the zoomed viewport (more labels fit when zoomed in).
  const xDataZoom = options?.dataZoom?.find((dz) => dz.id === "xAxis-slider");
  let xMin: number | undefined;
  let xMax: number | undefined;
  if (xDataZoom) {
    const fullMax = Math.max(...series.map((s) => s.x[1]));
    const axisMin: number = options?.xAxis?.[0]?.min ?? 0;
    const axisMax: number = options?.xAxis?.[0]?.max ?? fullMax;
    const axisRange = axisMax - axisMin;
    xMin =
      xDataZoom.startValue !== undefined
        ? xDataZoom.startValue
        : xDataZoom.start !== undefined
        ? (xDataZoom.start / 100) * axisRange + axisMin
        : undefined;
    xMax =
      xDataZoom.endValue !== undefined
        ? xDataZoom.endValue
        : xDataZoom.end !== undefined
        ? (xDataZoom.end / 100) * axisRange + axisMin
        : undefined;
  }

  const labelsToUse = getMosaicLabels(
    series,
    layout,
    screenshotSettings?.width,
    options?.grid?.[0]?.right ?? options?.grid?.right ?? MOSAIC_GRID_RIGHT,
    options?.grid?.[0]?.left ?? options?.grid?.left ?? MOSAIC_GRID_LEFT,
    screenshotSettings?.axisLabelFontSize,
    rotation,
    xMin,
    xMax
  );
  if (screenshotOptions.xAxis && screenshotOptions.xAxis.length > 0) {
    screenshotOptions.xAxis[0] = {
      ...screenshotOptions.xAxis[0],
      axisLabel: {
        ...screenshotOptions.xAxis[0].axisLabel,
        customValues: labelsToUse.map((l) => l.value),
        formatter: (val) => {
          const found = labelsToUse.findIndex((x) => x.value == val);
          if (found >= 0) {
            return labelsToUse[found].label;
          }
          return "";
        }
      },
      axisTick: {
        customValues: labelsToUse.map((l) => l.value)
      }
    };
  }
  return labelsToUse;
};

export const adjustXAxisForChart = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  screenshotOptions: any,
  screenshotSettings: IChartScreenshotSettings,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiResponse: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any
) => {
  const chartType: EvaChart = options?.chartType;
  const rotation: number = screenshotSettings?.xLabelRotation ?? 0;
  const fontSize: number =
    screenshotSettings?.axisLabelFontSize ?? DEFAULT_AXIS_LABEL_FONT_SIZE;
  const fontFamily: string = resolveFontFamily(screenshotSettings?.fontFamily);

  // === BoxPlot: recalculate label width or remove truncation for rotation ===
  if (IS_BOX_PLOT_CHART(chartType)) {
    if (rotation > 0) {
      screenshotOptions.xAxis.forEach((axis) => {
        if (axis.axisLabel) {
          axis.axisLabel.width = undefined;
          axis.axisLabel.overflow = undefined;
        }
      });
    } else {
      const xCategories = new Set(screenshotOptions?.series[0]?.data.map((s) => s.name));
      const width = calculateBoxPlotBarWidth(
        screenshotSettings?.width,
        xCategories?.size
      );
      screenshotOptions.xAxis.forEach((axis) => {
        if (axis.axisLabel) {
          axis.axisLabel.width = width;
        }
      });
    }
  }

  // === Mosaic: recalculate which labels to show (rotation-aware) ===
  if (IS_MOSAIC_CHART(chartType)) {
    applyMosaicLabels(
      screenshotOptions,
      screenshotSettings,
      apiResponse,
      options,
      rotation
    );
  }

  // === Rotation: apply to BoxPlot, Mosaic, StackedBar ===
  if (
    IS_BOX_PLOT_CHART(chartType) ||
    IS_MOSAIC_CHART(chartType) ||
    IS_STACKED_BAR_CHART(chartType)
  ) {
    // Always write rotate/align so switching back to 0° clears any previously-set rotation
    // (each screenshot cycle starts from getOption() which retains the previous setOption result)
    const applyRotationToAxis = (axis) => {
      if (!axis.axisLabel) axis.axisLabel = {};
      axis.axisLabel.rotate = rotation;
      axis.axisLabel.align = rotation > 0 ? "right" : "center";
    };

    if (Array.isArray(screenshotOptions.xAxis)) {
      screenshotOptions.xAxis.forEach((axis) => {
        if (!axis.position || axis.position === "bottom") {
          applyRotationToAxis(axis);
        }
      });
    } else if (screenshotOptions.xAxis) {
      applyRotationToAxis(screenshotOptions.xAxis);
    }
  }

  if (
    rotation > 0 &&
    (IS_BOX_PLOT_CHART(chartType) ||
      IS_MOSAIC_CHART(chartType) ||
      IS_STACKED_BAR_CHART(chartType))
  ) {
    const radians = (rotation * Math.PI) / 180;

    // Collect visible label texts to compute max width
    let visibleLabels: string[] = [];
    if (IS_MOSAIC_CHART(chartType)) {
      const mosaicAxis = Array.isArray(screenshotOptions.xAxis)
        ? screenshotOptions.xAxis[0]
        : screenshotOptions.xAxis;
      const customValues: unknown[] = mosaicAxis?.axisLabel?.customValues ?? [];
      const formatter = mosaicAxis?.axisLabel?.formatter;
      visibleLabels = customValues.map((v) =>
        typeof formatter === "function" ? String(formatter(v)) : String(v)
      );
    } else if (Array.isArray(screenshotOptions.xAxis)) {
      visibleLabels = (screenshotOptions.xAxis[0]?.data ?? []).map(String);
    } else {
      visibleLabels = (screenshotOptions.xAxis?.data ?? []).map(String);
    }

    const maxLabelTextWidth = visibleLabels.reduce((max, label) => {
      const w = calculateTextDimensions(label, fontSize, fontFamily).width;
      return Math.max(max, w);
    }, 0);

    // Use the actual rendered label height (includes line-height, not just fontSize)
    // so that rotatedLabelHeight and the grid-bottom delta are accurate at any font size.
    const labelHeight = calculateTextDimensions("1", fontSize, fontFamily).height;

    // Vertical space consumed by rotated labels below the axis line
    const rotatedLabelHeight =
      maxLabelTextWidth * Math.sin(radians) + labelHeight * Math.cos(radians);

    const NAME_GAP_PADDING = 8;

    const newNameGap = rotatedLabelHeight + NAME_GAP_PADDING;

    if (screenshotOptions.grid) {
      // grid.bottom was set by applySpacingToChartOptions using getXAxisNameGap,
      // which returns (labelHeight + NAME_GAP_PADDING). The nameGap is growing
      // from that baseline to newNameGap, so grid.bottom must grow by the same delta.
      const baseNameGap = labelHeight + NAME_GAP_PADDING;
      const rotationExtra = Math.max(0, newNameGap - baseNameGap);
      screenshotOptions.grid.bottom =
        (screenshotOptions.grid.bottom ?? 0) + rotationExtra;
    }

    // Push the x-axis title below the rotated labels
    if (Array.isArray(screenshotOptions.xAxis)) {
      screenshotOptions.xAxis.forEach((axis) => {
        if (!axis.position || axis.position === "bottom") {
          axis.nameGap = newNameGap;
        }
      });
    } else if (screenshotOptions.xAxis) {
      screenshotOptions.xAxis.nameGap = newNameGap;
    }

    // At 45° the first label's text extends left of its tick — expand grid.left if needed
    if (rotation < 90 && maxLabelTextWidth > 0) {
      const leftOverhang = maxLabelTextWidth * Math.cos(radians);
      const currentGridLeft: number = screenshotOptions.grid?.left ?? 0;
      if (leftOverhang > currentGridLeft) {
        screenshotOptions.grid.left = Math.ceil(leftOverhang) + 4;
      }
    }
  }
};
